import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FormGroup, useForm, Dropzone, Icon, useFileParser, Select, CustomSwitch} from '../components';
import { useTranslation } from 'react-i18next';
import {Modal, Button, Form, Col, Row, Container} from 'react-bootstrap';
import {COLORS} from "../theme";
import styled from "styled-components";
import SweetAlert from "react-bootstrap-sweetalert";

const BlockDivider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${COLORS.darkGrey};
  color: ${COLORS.primary};
  margin-bottom: 10px;
  margin-top: 10px;
  text-transform: uppercase;
`;

function CoordinatesImportModal(props) {
  const [file, setFile] = useState(props.file)
  const {t} = useTranslation();
  const [showParsingErrorAlert, setShowParsingErrorAlert] = useState(false)

  const { parsed, parsedData, parsingErrors, setFileToParse, parsingAttributes, FilePreview } = useFileParser(props.file)
  // Check if parsing done twice when a file is passed in props

  const {formData, getInputProps} = useForm({
    defaultObject: null,
    initialValues: {
      hasHeader: '',
      keyCol: '',
      latCol: '',
      lonCol: '',
      radiusCol: '',
      data: []
    }
  })

  useEffect(() => {
    setFileToParse(file)
  }, [file])

  useEffect(() => {
    setFile(props.file)
  }, [props.file])

  const handleNewFile = file => {
    setFile(file)
  }

  useEffect(() => {
    parsed && getInputProps("hasHeader").onChange(parsingAttributes.headerDetected)
  }, [parsingAttributes])

  useEffect(() => {
    parsingErrors && setShowParsingErrorAlert(true)
  }, [parsingErrors])

  const handleModalConfirm = () => setShowParsingErrorAlert(false)

  const ColumnSelect = (props) => {
    if (formData.hasHeader) {
      return <Select {...props} isClearable={true} >
        {parsedData[0].map((x, index) => (<Option key={index} value={x}>{x}</Option>))}
      </Select>
    } else {
      return <Select {...props} isClearable={true} >
        {[...Array(parsingAttributes.columns).keys()].map(x => (<Option key={x} value={x}>{x + 1}</Option>))}
      </Select>
    }
  }

  const handleCancel = () => {
    setFile(null)
  }

  const handleSubmit = () => {
    let content, columnMatching
    if (formData.hasHeader) {
      content = parsedData.slice(1)
      columnMatching = {
        key: parsingAttributes.headers.indexOf(formData.keyCol),
        latitude: parsingAttributes.headers.indexOf(formData.latCol),
        longitude: parsingAttributes.headers.indexOf(formData.lonCol),
        radius: parsingAttributes.headers.indexOf(formData.radiusCol)
      }
    } else {
      content = parsedData
      columnMatching = {
        key: parseInt(formData.keyCol),
        latitude: parseInt(formData.latCol),
        longitude: parseInt(formData.lonCol),
        radius: parseInt(formData.radiusCol)
      }
    }

    const data = content.map( row => ({
      key: row[columnMatching.key],
      latitude: row[columnMatching.latitude],
      longitude: row[columnMatching.longitude],
      radius: row[columnMatching.radius]
    }))
    props.handleSubmit(data)
  }

  return (
    <React.Fragment>
      <Modal show={props.show && (!props.file || !parsingErrors)} onHide={props.handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t("zone.coordinates.importTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {parsed && <div style={{position: "relative"}}>
            <FormGroup input={CustomSwitch} label={t("zone.coordinates.hasHeader")} {...getInputProps("hasHeader")}/>
            {typeof(formData.hasHeader) === 'boolean' && <React.Fragment>
              <BlockDivider>{t("zone.coordinates.columnSelection")}</BlockDivider>
              <Form.Row>
                <FormGroup input={ColumnSelect} label={t("zone.coordinates.key")} as={Col} md={6} {...getInputProps("keyCol")}/>
                <FormGroup input={ColumnSelect} label={t("latitude")} as={Col} md={6} {...getInputProps("latCol")}/>
              </Form.Row>
              <Form.Row>
                <FormGroup input={ColumnSelect} label={t("longitude")} as={Col} md={6} {...getInputProps("lonCol")}/>
                <FormGroup input={ColumnSelect} label={t("radius")} as={Col} md={6} {...getInputProps("radiusCol")}/>
              </Form.Row>
            </React.Fragment>}
            {parsed && <React.Fragment>
              <BlockDivider>{t("preview")}</BlockDivider>
              <FilePreview
                style={{marginBottom: 0}}
                maxLength={5}
                header={formData.hasHeader ? null : [...Array(parsingAttributes.columns).keys()].map(x => x+1)}
              />
              {parsedData.length > 5 && <div style={{
                position: "absolute",
                height: "50px",
                background: `linear-gradient(to top, ${COLORS.lightGrey}FF, ${COLORS.lightGrey}00)`,
                bottom: 0,
                width: "100%"
              }}/>}
            </React.Fragment>}
          </div>}
          {!parsed && <Container>
            <Dropzone
              as={Row}
              className="justify-content-center align-items-center"
              style={{height: "200px"}}
              multiple={false}
              onDrop={handleNewFile}>
              <div><Icon icon={"csv"} size={"4x"} variant={"primary"}/></div>
              <div style={{marginTop: '20px'}}>{t("zone.coordinates.importDescription")}</div>
            </Dropzone>
          </Container>}
        </Modal.Body>
        {parsed && <Modal.Footer>
          {<Button variant="secondary" onClick={handleCancel}>
            {t("cancel")}
          </Button>}
          {<Button variant="primary" onClick={handleSubmit}>
            {t("save")}
          </Button>}
        </Modal.Footer>}
      </Modal>
      {showParsingErrorAlert && parsingErrors && <SweetAlert
        warning
        title="Parsing error"
        confirmBtnBsStyle="light"
        onConfirm={handleModalConfirm}>
        {parsingErrors.message}
      </SweetAlert>}
    </React.Fragment>
  )
}

export default CoordinatesImportModal

CoordinatesImportModal.propTypes = {
  // file: PropTypes.file,
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired
}
